import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { BouncingLoader } from 'examkrackers-components'

import QuizModal from 'modules/books/components/aiSpaceRepetition/QuizModal'
import SpaceRepetitionModal from 'modules/books/components/aiSpaceRepetition/SpaceRepetitionModal'
import { useOpenSrqQuizFlow } from 'modules/books/components/aiSpaceRepetition/useOpenSrqQuizFlow'
import { useNavigateToQuizDiagnosticsBook } from '../../hooks/useNavigateToQuizDiagnosticsBook'

import { QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH } from '../../utils/quizDiagnosticsBreakpoints'
import {
  CHAPTER_VIEW_BAR_TOP_OFFSET,
  CHAPTER_VIEW_EXAMPLE_LEGEND_WIDTH,
  CHAPTER_VIEW_ROW_GAP,
  CHAPTER_VIEW_SECTION_CARD_WIDTH,
  CHAPTER_VIEW_SUMMARY_BAR_CONTENT_WIDTH,
  CHAPTER_VIEW_SUMMARY_CARD_WIDTH,
  CHAPTER_VIEW_ZERO_SCORE_BACKGROUND,
  CHAPTER_VIEW_ZERO_SCORE_BACKGROUND_OPACITY,
  CHAPTER_VIEW_BOOK_BAR_WIDTH,
  CHAPTER_VIEW_CHAPTER_BAR_WIDTH,
  CHAPTER_VIEW_SECTION_BAR_WIDTH
} from '../constants'
import { ChapterViewDiagnostics, ChapterViewSection } from '../types'
import { packSectionRows } from '../utils/packSectionRows'
import ChapterViewCardShell, {
  ChapterViewBarArea,
  ChapterViewCardBody,
  ChapterViewCardTitle
} from './ChapterViewCardShell'
import ChapterViewDashedLines from './ChapterViewDashedLines'
import ChapterViewProgressBar from './ChapterViewProgressBar'
import QuizDiagnosticsExampleLegend from '../../components/QuizDiagnosticsExampleLegend'

type QuizDiagnosticsChapterSectionsGridProps = {
  chapterData: ChapterViewDiagnostics
  isLoading: boolean
  hasError: boolean
  onRefetch: () => void
}

const QuizDiagnosticsChapterSectionsGrid = ({
  chapterData,
  isLoading,
  hasError,
  onRefetch
}: QuizDiagnosticsChapterSectionsGridProps): JSX.Element => {
  const { t } = useTranslation()
  const { navigateToBookSection } = useNavigateToQuizDiagnosticsBook()
  const [activeQuizSection, setActiveQuizSection] =
    useState<ChapterViewSection | null>(null)
  const [isSpaceRepetitionModalOpen, setIsSpaceRepetitionModalOpen] =
    useState(false)
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false)

  const handleOpenQuizModal = useCallback(() => {
    setIsQuizModalOpen(true)
  }, [])

  const handleOpenStartModal = useCallback(() => {
    setIsSpaceRepetitionModalOpen(true)
  }, [])

  const handleOpenSrqQuizFlow = useOpenSrqQuizFlow(
    handleOpenQuizModal,
    handleOpenStartModal
  )

  if (isLoading) {
    return (
      <LoaderContainer>
        <BouncingLoader />
      </LoaderContainer>
    )
  }

  if (hasError) {
    return (
      <MessageContainer role='alert'>
        {t('quizDiagnostics.chapterView.error')}
      </MessageContainer>
    )
  }

  const {
    tag,
    tagColour,
    bookAverage,
    chapterAverage,
    chapterOrder,
    studentBookId,
    chapterSrqRedDotsVisible,
    sections
  } = chapterData

  const handleSectionTitleClick = (section: ChapterViewSection) => {
    navigateToBookSection({
      studentBookId,
      chapterOrder,
      sectionPart: section.sectionPart,
      sectionId: section.id
    })
  }

  const handleSectionBarClick = (section: ChapterViewSection) => {
    setActiveQuizSection(section)
    handleOpenSrqQuizFlow()
  }

  const handleQuizModalOpen = () => {
    setIsSpaceRepetitionModalOpen(false)
    setIsQuizModalOpen(true)
  }

  const handleCloseSpaceModal = () => {
    setIsSpaceRepetitionModalOpen(false)
    setActiveQuizSection(null)
  }

  const handleCloseQuizModal = () => {
    setIsQuizModalOpen(false)
    setActiveQuizSection(null)
    onRefetch()
  }

  const sectionRows = packSectionRows(sections)
  const firstRow = sectionRows[0]
  const continuationRows = sectionRows.slice(1)
  const firstRowSections = true

  const renderSectionItem = (
    section: ChapterViewSection,
    firstRowSections?: boolean
  ) => {
    const isZeroScore = section.fiveQuizAverage === 0

    const sectionTitle = `${tag} ${chapterOrder}.${section.sectionOrder}`

    return (
      <SectionItem
        key={section.id}
        $isZeroScore={isZeroScore}
        $firstRowSections={firstRowSections}
      >
        <ChapterViewDashedLines topOffset={CHAPTER_VIEW_BAR_TOP_OFFSET} />
        <ChapterViewBarArea $contentWidth={CHAPTER_VIEW_SECTION_CARD_WIDTH}>
          <ChapterViewProgressBar
            barAriaLabel={t('quizDiagnostics.sectionBar.startQuiz', {
              section: sectionTitle
            })}
            color={tagColour}
            contentWidth={CHAPTER_VIEW_SECTION_CARD_WIDTH}
            fillVariant='chapter'
            barWidth={CHAPTER_VIEW_SECTION_BAR_WIDTH}
            hasQuizDueToday={Boolean(section.hasQuizDueToday)}
            chapterSrqRedDotsVisible={chapterSrqRedDotsVisible}
            sectionSrqRedDotsVisible={section.srqRedDotsVisible ?? true}
            onBarClick={() => handleSectionBarClick(section)}
            onTitleClick={() => handleSectionTitleClick(section)}
            percent={section.fiveQuizAverage}
            showTitleUnderline
            subtitle={section.title}
            title={sectionTitle}
            titleAriaLabel={t('quizDiagnostics.sectionBar.openBookSection', {
              section: sectionTitle
            })}
          />
        </ChapterViewBarArea>
      </SectionItem>
    )
  }

  return (
    <GridContainer>
      <TopRow>
        <ExampleLegendSlot>
          <QuizDiagnosticsExampleLegend variant='section' />
        </ExampleLegendSlot>

        <ChapterViewCardShell width={CHAPTER_VIEW_SUMMARY_CARD_WIDTH}>
          <ChapterViewCardTitle>
            {t('quizDiagnostics.chapterView.allTitle', { tag })}
          </ChapterViewCardTitle>
          <ChapterViewCardBody>
            <ChapterViewDashedLines topOffset={CHAPTER_VIEW_BAR_TOP_OFFSET} />
            <ChapterViewBarArea
              $contentWidth={CHAPTER_VIEW_SUMMARY_BAR_CONTENT_WIDTH}
            >
              <ChapterViewProgressBar
                color={tagColour}
                contentWidth={CHAPTER_VIEW_SUMMARY_BAR_CONTENT_WIDTH}
                fillVariant='chapter'
                barWidth={CHAPTER_VIEW_BOOK_BAR_WIDTH}
                percent={bookAverage}
                showTitleUnderline={false}
                subtitle={t('quizDiagnostics.all.labelDescription')}
                title={t('quizDiagnostics.chapterView.bookLabel', { tag })}
              />
            </ChapterViewBarArea>
          </ChapterViewCardBody>
        </ChapterViewCardShell>

        <ChapterViewCardShell width={CHAPTER_VIEW_SUMMARY_CARD_WIDTH}>
          <ChapterViewCardTitle>
            {t('quizDiagnostics.chapterView.chapterCardTitle', {
              tag,
              order: chapterOrder
            })}
          </ChapterViewCardTitle>
          <ChapterViewCardBody>
            <ChapterViewDashedLines topOffset={CHAPTER_VIEW_BAR_TOP_OFFSET} />
            <ChapterViewBarArea
              $contentWidth={CHAPTER_VIEW_SUMMARY_BAR_CONTENT_WIDTH}
            >
              <ChapterViewProgressBar
                color={tagColour}
                contentWidth={CHAPTER_VIEW_SUMMARY_BAR_CONTENT_WIDTH}
                fillVariant='chapter'
                barWidth={CHAPTER_VIEW_CHAPTER_BAR_WIDTH}
                percent={chapterAverage}
                showTitleUnderline={false}
                subtitle={t('quizDiagnostics.chapterView.chapterBarSubtitle')}
                title={t('quizDiagnostics.chapterView.chapterBarTitle', {
                  tag,
                  order: chapterOrder
                })}
              />
            </ChapterViewBarArea>
          </ChapterViewCardBody>
        </ChapterViewCardShell>

        {firstRow ? (
          <ChapterViewCardShell
            width={firstRow?.sections?.length * CHAPTER_VIEW_SECTION_CARD_WIDTH}
          >
            <ChapterViewCardTitle>
              {t('quizDiagnostics.chapterView.sectionsTitle', {
                tag,
                order: chapterOrder
              })}
            </ChapterViewCardTitle>
            <SectionsBody>
              <SectionsRow>
                {firstRow?.sections?.map(section =>
                  renderSectionItem(section, firstRowSections)
                )}
              </SectionsRow>
            </SectionsBody>
          </ChapterViewCardShell>
        ) : null}
      </TopRow>

      {continuationRows.map((row, rowIndex) => (
        <ContinuationRow key={`chapter-view-section-row-${rowIndex + 1}`}>
          <ChapterViewCardShell
            width={row?.sections?.length * CHAPTER_VIEW_SECTION_CARD_WIDTH}
          >
            <SectionsBody>
              <SectionsRow>
                {row?.sections?.map(section => renderSectionItem(section))}
              </SectionsRow>
            </SectionsBody>
          </ChapterViewCardShell>
        </ContinuationRow>
      ))}
      {isSpaceRepetitionModalOpen ? (
        <SpaceRepetitionModal
          isOpen={isSpaceRepetitionModalOpen}
          onClose={handleCloseSpaceModal}
          setQuizModalOpen={handleQuizModalOpen}
        />
      ) : null}
      {isQuizModalOpen && activeQuizSection ? (
        <QuizModal
          isOpen={isQuizModalOpen}
          onClose={handleCloseQuizModal}
          studentBookSubchapterId={activeQuizSection.id}
          studentBookChapterId={chapterData.chapterId}
          bookAbbreviation={tag}
          chapterOrder={chapterOrder}
          sectionOrder={String(activeQuizSection.sectionOrder)}
          sectionTitle={activeQuizSection.title}
        />
      ) : null}
    </GridContainer>
  )
}

export default QuizDiagnosticsChapterSectionsGrid

const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${CHAPTER_VIEW_ROW_GAP}px;
  width: 100%;
  min-width: 0;
  padding-bottom: 16px;
`

const TopRow = styled.div`
  display: grid;
  grid-template-columns:
    ${CHAPTER_VIEW_EXAMPLE_LEGEND_WIDTH}px
    ${CHAPTER_VIEW_SUMMARY_CARD_WIDTH}px
    ${CHAPTER_VIEW_SUMMARY_CARD_WIDTH}px
    auto;
  gap: ${CHAPTER_VIEW_ROW_GAP}px;
  width: 100%;
  min-width: 0;
  align-items: stretch;

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    grid-template-columns: 1fr;
  }
`

const ExampleLegendSlot = styled.div`
  display: flex;
  align-items: stretch;
  min-width: 0;

  > * {
    width: 100%;
    min-height: 100%;
  }
`

const ContinuationRow = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`

const SectionsBody = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
`

const SectionsRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
  min-width: 0;
  flex: 1;
  overflow: visible;
`

const SectionItem = styled.div<{ $isZeroScore: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex: 0 0 ${CHAPTER_VIEW_SECTION_CARD_WIDTH}px;
  width: ${CHAPTER_VIEW_SECTION_CARD_WIDTH}px;
  min-width: ${CHAPTER_VIEW_SECTION_CARD_WIDTH}px;
  max-width: ${CHAPTER_VIEW_SECTION_CARD_WIDTH}px;
  align-self: ${({ $isZeroScore }) =>
    $isZeroScore ? 'stretch' : 'flex-start'};
  box-sizing: border-box;

  &:hover,
  &:focus-within {
    z-index: ${({ theme }) => theme.zIndex.tooltip};
  }

  ${({ $isZeroScore }) =>
    $isZeroScore &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: ${({ $firstRowSections }) => (!$firstRowSections ? 0 : -24)}px;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: ${CHAPTER_VIEW_SECTION_CARD_WIDTH}px;
        background-color: ${CHAPTER_VIEW_ZERO_SCORE_BACKGROUND};
        opacity: ${CHAPTER_VIEW_ZERO_SCORE_BACKGROUND_OPACITY};
        z-index: 0;
        pointer-events: none;
      }
    `}

  ${ChapterViewBarArea} {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    flex: 1;
  }
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 240px;
  width: 100%;
`

const MessageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 160px;
  width: 100%;
  font-family: Arial, sans-serif;
  font-size: 14px;
  color: #444444;
  text-align: center;
`
